import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setTheme } from '../../store/slices/uiSlice'
import type { AppDispatch } from '../../store'
import {
  Card,
  Row,
  Col,
  Form,
  Input,
  Button,
  Switch,
  Select,
  Divider,
  Tabs,
  message,
  Avatar,
  Upload,
  Radio,
  InputNumber,
  Modal,
} from 'antd'
import {
  UserOutlined,
  BellOutlined,
  SafetyOutlined,
  ApiOutlined,
  CloudUploadOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useAuth } from '../../contexts/AuthContext'
import { userApi, systemApi, settingsApi } from '../../services/api'

const { Option } = Select

const PROFILE_STORAGE_KEY = 'asset_settings_profile'
const AVATAR_STORAGE_KEY = 'asset_settings_avatar'
const NOTIFICATION_STORAGE_KEY = 'asset_settings_notification'
const SYSTEM_STORAGE_KEY = 'asset_settings_system'

const defaultProfile = {
  name: '燕子',
  email: 'yanzi@example.com',
  phone: '13800138000',
  department: '技术部',
  position: '数据分析师',
  bio: '专注于数据可视化和资产管理的技术专家',
}

const Settings = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { logout } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [form] = Form.useForm()
  const [notificationForm] = Form.useForm()
  const [systemForm] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState('')
  const [backupLoading, setBackupLoading] = useState(false)
  const [checkUpdateLoading, setCheckUpdateLoading] = useState(false)

  // 通知开关状态（可控，可持久化）
  const [notificationSettings, setNotificationSettings] = useState([
    { key: 'email', title: '邮件通知', description: '重要事件通过邮件通知', enabled: true },
    { key: 'push', title: '推送通知', description: '实时推送重要提醒', enabled: true },
    { key: 'sms', title: '短信通知', description: '紧急情况短信通知', enabled: false },
    { key: 'daily', title: '每日摘要', description: '每天发送资产摘要报告', enabled: true },
    { key: 'weekly', title: '每周报告', description: '每周发送详细分析报告', enabled: true },
  ])

  // 从接口或 localStorage 加载个人资料
  useEffect(() => {
    userApi.getProfile()
      .then((res: any) => {
        const data = res?.data ?? res
        if (data && typeof data === 'object') {
          form.setFieldsValue({
            name: data.name ?? data.nickname ?? defaultProfile.name,
            email: data.email ?? defaultProfile.email,
            phone: data.phone ?? defaultProfile.phone,
            department: data.department ?? defaultProfile.department,
            position: data.position ?? defaultProfile.position,
            bio: data.bio ?? defaultProfile.bio,
          })
          const avatar = (data as any).avatar ?? (data as any).avatarUrl ?? (data as any).avatar_url
          if (typeof avatar === 'string' && avatar) setAvatarUrl(avatar)
        }
      })
      .catch(() => {
        try {
          const profileJson = localStorage.getItem(PROFILE_STORAGE_KEY)
          if (profileJson) form.setFieldsValue(JSON.parse(profileJson))
          else form.setFieldsValue(defaultProfile)
        } catch {
          form.setFieldsValue(defaultProfile)
        }
      })
    const savedAvatar = localStorage.getItem(AVATAR_STORAGE_KEY)
    if (savedAvatar) setAvatarUrl(savedAvatar)
  }, [form])

  useEffect(() => {
    const defaults = { dailyTime: '08:00', weeklyDay: 'friday', channel: 'all' }
    settingsApi.notificationsGet()
      .then((res: any) => {
        const data = res?.data ?? res
        if (data && typeof data === 'object') {
          if (Array.isArray((data as any).settings)) {
            setNotificationSettings((data as any).settings)
          }
          notificationForm.setFieldsValue({
            dailyTime: (data as any).dailyTime ?? defaults.dailyTime,
            weeklyDay: (data as any).weeklyDay ?? defaults.weeklyDay,
            channel: (data as any).channel ?? defaults.channel,
          })
          return
        }
        throw new Error('invalid notifications data')
      })
      .catch(() => {
        try {
          const json = localStorage.getItem(NOTIFICATION_STORAGE_KEY)
          if (json) {
            const data = JSON.parse(json)
            if (Array.isArray(data.settings)) setNotificationSettings(data.settings)
            notificationForm.setFieldsValue({
              dailyTime: data.dailyTime ?? defaults.dailyTime,
              weeklyDay: data.weeklyDay ?? defaults.weeklyDay,
              channel: data.channel ?? defaults.channel,
            })
          } else {
            notificationForm.setFieldsValue(defaults)
          }
        } catch {
          notificationForm.setFieldsValue(defaults)
        }
      })
  }, [notificationForm])

  // 从接口或 localStorage 加载系统设置，并同步主题到 Redux（仅初始化一次）
  useEffect(() => {
    systemApi.getSettings()
      .then((res: any) => {
        const data = res?.data ?? res
        if (data && typeof data === 'object') {
          systemForm.setFieldsValue(data)
          const theme = data.theme
          if (theme === 'light' || theme === 'dark' || theme === 'auto') {
            dispatch(setTheme(theme))
          }
        }
      })
      .catch(() => {
        try {
          const json = localStorage.getItem(SYSTEM_STORAGE_KEY)
          if (json) {
            const data = JSON.parse(json)
            systemForm.setFieldsValue(data)
            const theme = data?.theme
            if (theme === 'light' || theme === 'dark' || theme === 'auto') {
              dispatch(setTheme(theme))
            }
          }
        } catch {
          // ignore
        }
      })
  }, [systemForm, dispatch])

  // 从仪表盘快速操作「设置提醒」或头部「个人资料/账户设置」进入时切换到对应标签
  useEffect(() => {
    const state = location.state as { tab?: string } | null
    if (state?.tab === 'notifications' || state?.tab === 'profile' || state?.tab === 'security' || state?.tab === 'system') {
      setActiveTab(state.tab)
      window.history.replaceState({}, '', location.pathname)
    }
  }, [location.state, location.pathname])

  const handleSaveProfile = () => {
    setLoading(true)
    form.validateFields().then(values => {
      userApi.updateProfile(values)
        .then(() => {
          localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(values))
          message.success('个人资料保存成功')
        })
        .catch(() => {
          localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(values))
          message.success('个人资料已保存到本地')
        })
        .finally(() => setLoading(false))
    }).catch(() => setLoading(false))
  }

  const handleSaveNotification = () => {
    setLoading(true)
    notificationForm.validateFields().then(values => {
      const payload = {
        settings: notificationSettings,
        dailyTime: values.dailyTime,
        weeklyDay: values.weeklyDay,
        channel: values.channel,
      }
      settingsApi.notificationsUpdate(payload as any)
        .then(() => {
          localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(payload))
          message.success('通知设置保存成功')
        })
        .catch(() => {
          localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(payload))
          message.success('通知设置已保存到本地')
        })
        .finally(() => setLoading(false))
    }).catch(() => setLoading(false))
  }

  const handleSaveSystem = () => {
    setLoading(true)
    systemForm.validateFields().then(values => {
      systemApi.updateSettings(values)
        .then(() => {
          localStorage.setItem(SYSTEM_STORAGE_KEY, JSON.stringify(values))
          if (values.theme === 'light' || values.theme === 'dark' || values.theme === 'auto') {
            dispatch(setTheme(values.theme))
          }
          message.success('系统设置保存成功')
        })
        .catch(() => {
          localStorage.setItem(SYSTEM_STORAGE_KEY, JSON.stringify(values))
          if (values.theme === 'light' || values.theme === 'dark' || values.theme === 'auto') {
            dispatch(setTheme(values.theme))
          }
          message.success('系统设置已保存到本地')
        })
        .finally(() => setLoading(false))
    }).catch(() => setLoading(false))
  }

  const toggleNotification = (key: string) => {
    setNotificationSettings(prev =>
      prev.map(s => (s.key === key ? { ...s, enabled: !s.enabled } : s))
    )
  }

  const [avatarLoading, setAvatarLoading] = useState(false)
  const handleAvatarChange = (info: any) => {
    const { file } = info
    const rawFile = file?.originFileObj ?? file
    if (!rawFile || !(rawFile instanceof File)) return
    const isImage = /^image\/(jpeg|png|webp)$/i.test(rawFile.type)
    if (!isImage) {
      message.error('仅支持 JPG、PNG、WEBP 格式')
      return
    }
    const isLt2M = rawFile.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB')
      return
    }
    setAvatarLoading(true)
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const result = reader.result as string
      setAvatarUrl(result)
      try {
        localStorage.setItem(AVATAR_STORAGE_KEY, result)
      } catch {
        message.warning('头像已更换，但未保存到本地（存储已满）')
      }
      setAvatarLoading(false)
      message.success('头像已更换')
    })
    reader.addEventListener('error', () => {
      setAvatarLoading(false)
      message.error('读取图片失败，请重试')
    })
    reader.readAsDataURL(rawFile)
  }

  const beforeAvatarUpload = (file: File) => {
    const isImage = /^image\/(jpeg|png|webp)$/i.test(file.type)
    if (!isImage) {
      message.error('仅支持 JPG、PNG、WEBP 格式')
      return Upload.LIST_IGNORE
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB')
      return Upload.LIST_IGNORE
    }
    return false
  }

  const handleLogoutCurrent = () => {
    Modal.confirm({
      title: '退出当前会话',
      content: '确定要退出登录吗？',
      okText: '确定退出',
      cancelText: '取消',
      onOk: () => {
        logout()
        navigate('/login', { replace: true })
        message.success('已退出登录')
      },
    })
  }

  const handleRevokeSession = () => {
    Modal.confirm({
      title: '撤销上次登录',
      content: '将使「上次登录」设备会话失效，该设备需重新登录。确定继续？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        settingsApi.securityRevokeSession({})
          .then(() => {
            message.success('已撤销该设备会话')
          })
          .catch(() => {
            message.success('已撤销该设备会话')
          })
      },
    })
  }

  const handleBackup = () => {
    setBackupLoading(true)
    systemApi.backupData()
      .then(() => message.success('数据备份已启动，备份完成后将通知您'))
      .catch(() => message.success('数据备份已提交'))
      .finally(() => setBackupLoading(false))
  }

  const handleCheckUpdate = () => {
    setCheckUpdateLoading(true)
    systemApi.checkUpdate()
      .then((res: any) => {
        const data = res?.data ?? res
        if (data && typeof data === 'object' && (data as any).message) {
          message.info(String((data as any).message))
        } else {
          message.info('已检查更新，当前为最新版本或无需更新')
        }
      })
      .catch(() => {
        message.error('检查更新失败，请稍后重试')
      })
      .finally(() => setCheckUpdateLoading(false))
  }

  const handleResetDefaults = () => {
    Modal.confirm({
      title: '恢复默认设置',
      content: '将恢复所有系统设置为默认值，是否继续？',
      okText: '确定恢复',
      cancelText: '取消',
      onOk: () => {
        systemApi.resetDefaults()
          .then(() => {
            localStorage.removeItem(SYSTEM_STORAGE_KEY)
            systemForm.resetFields()
            message.success('已恢复默认设置')
          })
          .catch(() => {
            localStorage.removeItem(SYSTEM_STORAGE_KEY)
            systemForm.resetFields()
            message.success('已恢复默认设置')
          })
      },
    })
  }

  const handlePasswordChange = () => {
    passwordForm.validateFields().then(values => {
      const payload = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      }
      setLoading(true)
      userApi.changePassword(payload)
        .then(() => {
          message.success('密码修改成功，请使用新密码重新登录')
          passwordForm.resetFields()
        })
        .catch((error: any) => {
          message.error(error?.friendlyMessage ?? '密码修改失败，请重试')
        })
        .finally(() => setLoading(false))
    })
  }

  const securitySettings = [
    {
      key: '2fa',
      title: '双重认证',
      description: '启用双重认证提高账户安全',
      enabled: true,
    },
    {
      key: 'password',
      title: '密码策略',
      description: '强制定期更换密码',
      enabled: false,
    },
    {
      key: 'session',
      title: '会话管理',
      description: '限制同时登录的设备数量',
      enabled: true,
    },
    {
      key: 'ip',
      title: 'IP白名单',
      description: '只允许特定IP地址访问',
      enabled: false,
    },
  ]

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold mb-2">系统设置</h1>
        <p className="text-gray-600">管理您的账户偏好和系统配置</p>
      </div>

      {/* 设置标签页 */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'profile',
              label: (
                <span>
                  <UserOutlined />
                  个人资料
                </span>
              ),
              children: (
                <Row gutter={[32, 32]}>
              <Col xs={24} lg={8}>
                <div className="space-y-4">
                  <div className="text-center">
                    <Avatar
                      size={128}
                      src={avatarUrl}
                      icon={<UserOutlined />}
                      className="mb-4 border-4 border-white shadow-lg"
                    />
                    <Upload
                      name="avatar"
                      showUploadList={false}
                      onChange={handleAvatarChange}
                      beforeUpload={beforeAvatarUpload}
                      accept=".jpg,.jpeg,.png,.webp"
                      disabled={avatarLoading}
                    >
                      <Button icon={<UploadOutlined />} loading={avatarLoading}>
                        更换头像
                      </Button>
                    </Upload>
                    <div className="text-sm text-gray-500 mt-2">
                      支持 JPG、PNG 格式，大小不超过 2MB
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} lg={16}>
                <Form form={form} layout="vertical" initialValues={defaultProfile}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="name"
                        label="姓名"
                        rules={[{ required: true, message: '请输入姓名' }]}
                      >
                        <Input placeholder="请输入姓名" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="email"
                        label="邮箱"
                        rules={[
                          { required: true, message: '请输入邮箱' },
                          { type: 'email', message: '请输入有效的邮箱地址' },
                        ]}
                      >
                        <Input placeholder="请输入邮箱" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="phone"
                        label="手机号"
                        rules={[{ required: true, message: '请输入手机号' }]}
                      >
                        <Input placeholder="请输入手机号" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="department"
                        label="部门"
                        rules={[{ required: true, message: '请输入部门' }]}
                      >
                        <Input placeholder="请输入部门" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="position"
                    label="职位"
                    rules={[{ required: true, message: '请输入职位' }]}
                  >
                    <Input placeholder="请输入职位" />
                  </Form.Item>

                  <Form.Item
                    name="bio"
                    label="个人简介"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="请输入个人简介"
                      maxLength={200}
                      showCount
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSaveProfile}
                      loading={loading}
                    >
                      保存更改
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            </Row>
              ),
            },
            {
              key: 'notifications',
              label: (
                <span>
                  <BellOutlined />
                  通知设置
                </span>
              ),
              children: (
            <div className="space-y-6">
              <div className="text-lg font-medium mb-4">通知偏好设置</div>
              
              {notificationSettings.map(setting => (
                <Card key={setting.key} size="small" className="mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{setting.title}</div>
                      <div className="text-gray-500 text-sm">{setting.description}</div>
                    </div>
                    <Switch checked={setting.enabled} onChange={() => toggleNotification(setting.key)} />
                  </div>
                </Card>
              ))}

              <Divider />

              <div className="text-lg font-medium mb-4">通知时间设置</div>
              <Form form={notificationForm} layout="vertical" initialValues={{ dailyTime: '08:00', weeklyDay: 'friday', channel: 'all' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="dailyTime" label="每日摘要发送时间">
                      <Select>
                        <Option value="07:00">07:00</Option>
                        <Option value="08:00">08:00</Option>
                        <Option value="09:00">09:00</Option>
                        <Option value="10:00">10:00</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="weeklyDay" label="每周报告发送日期">
                      <Select>
                        <Option value="monday">周一</Option>
                        <Option value="tuesday">周二</Option>
                        <Option value="wednesday">周三</Option>
                        <Option value="thursday">周四</Option>
                        <Option value="friday">周五</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <div className="text-lg font-medium mb-4">通知渠道</div>
                <Form.Item name="channel">
                  <Radio.Group>
                    <Radio value="all">所有渠道</Radio>
                    <Radio value="important">仅重要通知</Radio>
                    <Radio value="email">仅邮件</Radio>
                    <Radio value="none">关闭所有通知</Radio>
                  </Radio.Group>
                </Form.Item>

                <Button type="primary" onClick={handleSaveNotification} loading={loading}>
                  保存通知设置
                </Button>
              </Form>
            </div>
              ),
            },
            {
              key: 'security',
              label: (
                <span>
                  <SafetyOutlined />
                  安全设置
                </span>
              ),
              children: (
            <div className="space-y-6">
              <div className="text-lg font-medium mb-4">账户安全</div>
              
              {securitySettings.map(setting => (
                <Card key={setting.key} size="small" className="mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{setting.title}</div>
                      <div className="text-gray-500 text-sm">{setting.description}</div>
                    </div>
                    <Switch defaultChecked={setting.enabled} />
                  </div>
                </Card>
              ))}

              <Divider />

              <div className="text-lg font-medium mb-4">密码修改</div>
              <Form form={passwordForm} layout="vertical">
                <Form.Item
                  label="当前密码"
                  name="currentPassword"
                  rules={[{ required: true, message: '请输入当前密码' }]}
                >
                  <Input.Password placeholder="请输入当前密码" />
                </Form.Item>
                
                <Form.Item
                  label="新密码"
                  name="newPassword"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 8, message: '密码长度至少8位' },
                  ]}
                >
                  <Input.Password placeholder="请输入新密码" />
                </Form.Item>
                
                <Form.Item
                  label="确认新密码"
                  name="confirmPassword"
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve()
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'))
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="请确认新密码" />
                </Form.Item>
                
                <Form.Item>
                  <Button type="primary" onClick={handlePasswordChange} loading={loading}>
                    修改密码
                  </Button>
                </Form.Item>
              </Form>

              <Divider />

              <div className="text-lg font-medium mb-4">登录记录</div>
              <Card>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <div>
                      <div className="font-medium">当前会话</div>
                      <div className="text-sm text-gray-500">Chrome • Windows • 上海市</div>
                    </div>
                    <Button type="link" danger onClick={handleLogoutCurrent}>退出</Button>
                  </div>
                  <Divider />
                  <div className="flex justify-between">
                    <div>
                      <div className="font-medium">上次登录</div>
                      <div className="text-sm text-gray-500">Safari • iOS • 北京市 • 2024-01-14 16:30</div>
                    </div>
                    <Button type="link" danger onClick={handleRevokeSession}>撤销</Button>
                  </div>
                </div>
              </Card>
            </div>
              ),
            },
            {
              key: 'system',
              label: (
                <span>
                  <ApiOutlined />
                  系统设置
                </span>
              ),
              children: (
            <div className="space-y-6">
              <div className="text-lg font-medium mb-4">数据设置</div>
              
              <Card>
                <Form form={systemForm} layout="vertical" initialValues={{
                  dataRefresh: 'realtime',
                  dataRetention: '1year',
                  backupFreq: 'daily',
                  theme: 'light',
                  language: 'zh-CN',
                  timeFormat: '24h',
                  currency: 'CNY',
                  apiLimit: 10,
                  cacheSize: 100,
                  logLevel: 'info',
                }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="dataRefresh" label="数据刷新频率">
                        <Select>
                          <Option value="realtime">实时</Option>
                          <Option value="5min">5分钟</Option>
                          <Option value="15min">15分钟</Option>
                          <Option value="30min">30分钟</Option>
                          <Option value="1hour">1小时</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="dataRetention" label="数据保留时间">
                        <Select>
                          <Option value="3months">3个月</Option>
                          <Option value="6months">6个月</Option>
                          <Option value="1year">1年</Option>
                          <Option value="2years">2年</Option>
                          <Option value="forever">永久</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="backupFreq" label="数据备份频率">
                    <Radio.Group>
                      <Radio value="daily">每日</Radio>
                      <Radio value="weekly">每周</Radio>
                      <Radio value="monthly">每月</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item>
                    <Button icon={<CloudUploadOutlined />} onClick={handleBackup} loading={backupLoading}>
                      立即备份数据
                    </Button>
                  </Form.Item>
                </Form>
              </Card>

              <Divider />

              <div className="text-lg font-medium mb-4">显示设置</div>
              
              <Card>
                <Form form={systemForm} layout="vertical">
                  <Form.Item name="theme" label="主题模式">
                    <Radio.Group
                      onChange={(e) => {
                        const value = e.target.value as 'light' | 'dark' | 'auto'
                        systemForm.setFieldsValue({ theme: value })
                        dispatch(setTheme(value))
                        try {
                          const raw = localStorage.getItem(SYSTEM_STORAGE_KEY)
                          const prev = raw ? JSON.parse(raw) : {}
                          localStorage.setItem(SYSTEM_STORAGE_KEY, JSON.stringify({ ...prev, theme: value }))
                        } catch {}
                      }}
                    >
                      <Radio value="light">亮色模式</Radio>
                      <Radio value="dark">暗色模式</Radio>
                      <Radio value="auto">跟随系统</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item name="language" label="语言设置">
                    <Select>
                      <Option value="zh-CN">简体中文</Option>
                      <Option value="en-US">English</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="timeFormat" label="时间格式">
                    <Select>
                      <Option value="24h">24小时制</Option>
                      <Option value="12h">12小时制</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="currency" label="货币单位">
                    <Select>
                      <Option value="CNY">人民币 (¥)</Option>
                      <Option value="USD">美元 ($)</Option>
                      <Option value="EUR">欧元 (€)</Option>
                      <Option value="JPY">日元 (¥)</Option>
                    </Select>
                  </Form.Item>
                </Form>
              </Card>

              <Divider />

              <div className="text-lg font-medium mb-4">高级设置</div>
              
              <Card>
                <Form form={systemForm} layout="vertical">
                  <Form.Item name="apiLimit" label="API请求频率限制">
                    <InputNumber min={1} max={100} addonAfter="次/秒" />
                  </Form.Item>

                  <Form.Item name="cacheSize" label="缓存大小">
                    <InputNumber min={10} max={1000} addonAfter="MB" />
                  </Form.Item>

                  <Form.Item name="logLevel" label="日志级别">
                    <Select>
                      <Option value="debug">调试</Option>
                      <Option value="info">信息</Option>
                      <Option value="warn">警告</Option>
                      <Option value="error">错误</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" onClick={handleSaveSystem} loading={loading}>
                      保存系统设置
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </div>
              ),
            },
          ]}
        />
      </Card>

      {/* 系统信息 */}
      <Card title="系统信息">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <div className="text-gray-500 text-sm">系统版本</div>
            <div className="font-medium">v1.0.0</div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="text-gray-500 text-sm">最后更新时间</div>
            <div className="font-medium">2024-01-15</div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="text-gray-500 text-sm">数据总量</div>
            <div className="font-medium">1.2 GB</div>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <div className="text-gray-500 text-sm">用户数量</div>
            <div className="font-medium">156</div>
          </Col>
        </Row>
        
        <Divider />
        
        <div className="flex justify-between">
          <Button onClick={handleCheckUpdate} loading={checkUpdateLoading}>检查更新</Button>
          <Button type="primary" danger onClick={handleResetDefaults}>
            恢复默认设置
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default Settings